import { useState, useEffect, useCallback } from 'react';
import { type Pokemon, type GameState, LetterState, GameStatus, GameMode } from '@/types/pokemon';
import { type PokemonGeneration, DEFAULT_GENERATION } from '@/data/pokemonGenerations';

const POKEAPI_BASE = 'https://pokeapi.co/api/v2';

export function usePokemonGame() {
  const [gameState, setGameState] = useState<GameState>({
    targetPokemon: null,
    attempts: [],
    currentAttempt: '',
    status: GameStatus.PLAYING,
    gameMode: GameMode.BLUR,
  });

  const [selectedGeneration, setSelectedGeneration] = useState<PokemonGeneration>(DEFAULT_GENERATION);
  // Initialize gameMode from localStorage or default to BLUR
  const [gameMode, setGameMode] = useState<GameMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('pokemon-game-mode');
      return saved && Object.values(GameMode).includes(saved as GameMode) 
        ? (saved as GameMode) 
        : GameMode.BLUR;
    }
    return GameMode.BLUR;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get random Pokemon with French name
  const getRandomPokemon = async (): Promise<Pokemon> => {
    try {
      const pokemonCount = selectedGeneration.endId - selectedGeneration.startId + 1;
      const randomId = Math.floor(Math.random() * pokemonCount) + selectedGeneration.startId;
      
      // Fetch Pokemon data
      const pokemonResponse = await fetch(`${POKEAPI_BASE}/pokemon/${randomId}`);
      if (!pokemonResponse.ok) {
        throw new Error('Failed to fetch Pokemon');
      }
      const pokemonData = await pokemonResponse.json();
      
      // Fetch species data for French name
      const speciesResponse = await fetch(`${POKEAPI_BASE}/pokemon-species/${randomId}`);
      if (!speciesResponse.ok) {
        throw new Error('Failed to fetch Pokemon species');
      }
      const speciesData = await speciesResponse.json();
      
      // Find French name
      const frenchName = speciesData.names.find((name: any) => name.language.name === 'fr')?.name || pokemonData.name;
      
      return {
        ...pokemonData,
        frenchName: frenchName
      };
    } catch (err) {
      throw new Error('Failed to fetch random Pokemon');
    }
  };

  // Initialize game
  const initializeGame = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const pokemon = await getRandomPokemon();
      setGameState({
        targetPokemon: pokemon,
        attempts: [],
        currentAttempt: '',
        status: GameStatus.PLAYING,
        gameMode: gameMode,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize game');
    } finally {
      setLoading(false);
    }
  }, [selectedGeneration, gameMode]);

  // Normalize string by removing accents
  const normalizeString = (str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Check if guess is correct
  const checkGuess = (guess: string, target: string): LetterState[] => {
    const result: LetterState[] = new Array(guess.length).fill(LetterState.ABSENT);
    const normalizedTarget = normalizeString(target.toLowerCase());
    const normalizedGuess = normalizeString(guess.toLowerCase());
    const targetLetters = normalizedTarget.split('');
    const guessLetters = normalizedGuess.split('');
    
    // First pass: mark correct letters
    for (let i = 0; i < guessLetters.length; i++) {
      if (guessLetters[i] === targetLetters[i]) {
        result[i] = LetterState.CORRECT;
        targetLetters[i] = ''; // Mark as used
      }
    }
    
    // Second pass: mark present letters
    for (let i = 0; i < guessLetters.length; i++) {
      if (result[i] === LetterState.CORRECT) continue;
      
      const targetIndex = targetLetters.indexOf(guessLetters[i]);
      if (targetIndex !== -1) {
        result[i] = LetterState.PRESENT;
        targetLetters[targetIndex] = ''; // Mark as used
      }
    }
    
    return result;
  };

  // Submit guess
  const submitGuess = useCallback((guess: string) => {
    if (!gameState.targetPokemon || guess.length === 0) return;
    
    const target = gameState.targetPokemon.frenchName;
    const letterStates = checkGuess(guess, target);
    
    const newAttempts = [...gameState.attempts, guess];
    const isCorrect = letterStates.every(state => state === LetterState.CORRECT);
    
    // Determine game status
    let newStatus = GameStatus.PLAYING;
    if (isCorrect) {
      newStatus = GameStatus.WON;
    } else if (newAttempts.length >= 5) {
      newStatus = GameStatus.LOST;
    }
    
    setGameState(prev => ({
      ...prev,
      attempts: newAttempts,
      currentAttempt: '',
      status: newStatus,
    }));
  }, [gameState.targetPokemon, gameState.attempts]);

  // Update current attempt
  const updateCurrentAttempt = useCallback((attempt: string) => {
    setGameState(prev => ({
      ...prev,
      currentAttempt: attempt,
    }));
  }, []);

  // Restart game
  const restartGame = useCallback(() => {
    initializeGame();
  }, [initializeGame]);

  // Initialize game on mount
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  // Handle generation change
  const handleGenerationChange = useCallback((generation: PokemonGeneration) => {
    setSelectedGeneration(generation);
    // Game will automatically restart due to selectedGeneration dependency
  }, []);

  // Handle mode change
  const handleModeChange = useCallback((mode: GameMode) => {
    setGameMode(mode);
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('pokemon-game-mode', mode);
    }
    restartGame();
  }, [restartGame]);

  // Compute effect level from attempts (0-5, where 5 is clear/normal)
  const effectLevel = Math.min(5, gameState.attempts.length);

  return {
    gameState,
    effectLevel,
    selectedGeneration,
    gameMode,
    loading,
    error,
    submitGuess,
    updateCurrentAttempt,
    restartGame,
    handleGenerationChange,
    handleModeChange,
    checkGuess,
  };
}
